import {
  EnumProductEventMetadataTicketStructure,
  EnumProductType,
  Product,
} from "gql/generated";
import { getMultilingualString } from "shared/utils/getMultilingualString";
import { getResolvedProductSellingConfig } from "shared/utils/productSelling";
import type { MultilingualLangKey } from "shared/types";
import type {
  CartItem,
  StoredTierLine,
} from "widgets/booker/contexts/CartContextProvider/cartContextTypes";

export type SummaryLine = {
  key: string;
  label: string;
  amount: number;
  detail?: string;
};

type BuildSummaryLinesOptions = {
  selectedTierId?: string | null;
  language?: MultilingualLangKey;
  /** Event date / session count shown under each event line. */
  eventDetail?: string;
};

const qtyLabel = (name: string, quantity: number) =>
  quantity > 1 ? `${name} × ${quantity}` : name;

/** Merch rows always show the variant and count, e.g. "Holiday Toque – Red × 1". */
const merchLabel = (
  item: Pick<CartItem, "productName" | "merchVariantLabel" | "quantity">,
) =>
  `${item.merchVariantLabel ? `${item.productName} – ${item.merchVariantLabel}` : item.productName} × ${item.quantity}`;

/**
 * Receipt rows for merch, which has no stored tier lines: one row per
 * purchased variant from the order's own variant and quantity.
 */
export const merchItemToSummaryLines = (
  item: CartItem,
): SummaryLine[] | null =>
  item.productType === EnumProductType.Merch
    ? [
        {
          key: `${item.productId}-${item.merchVariantId ?? "base"}`,
          label: merchLabel(item),
          amount: item.total,
        },
      ]
    : null;

/**
 * Converts tier lines frozen on an order/invoice at write time into display
 * rows. Used on all post-purchase surfaces (receipt, invoices, PDFs) so the
 * document never depends on live product config. Returns null when the
 * record has no stored lines (older records fall back to a single
 * product-name row).
 */
export const storedTierLinesToSummaryLines = (
  item: Pick<CartItem, "productId" | "productName">,
  storedLines: StoredTierLine[] | null | undefined,
  options: Pick<BuildSummaryLinesOptions, "language" | "eventDetail"> = {},
): SummaryLine[] | null => {
  const lines = (storedLines ?? []).filter(line => !!line);
  if (lines.length === 0) return null;

  return lines.map((line, index) => {
    const name =
      getMultilingualString(line.name ?? {}, options.language ?? "en") ||
      item.productName;
    return {
      key: `${item.productId}-stored-${line.tierId ?? index}`,
      label: qtyLabel(name, line.quantity ?? 1),
      amount: line.amount ?? 0,
      detail: options.eventDetail,
    };
  });
};

/**
 * Builds priced summary rows for a cart item. When the product has multiple
 * price options (event packages, fee/renewable tiers), each selected option
 * becomes its own row instead of collapsing under the product/checkout name.
 */
export const buildSummaryLines = (
  item: CartItem,
  product?: Product | null,
  options: BuildSummaryLinesOptions = {},
): SummaryLine[] | null => {
  const language = options.language ?? "en";
  const selectedTierId = options.selectedTierId ?? item.selectedTierId ?? null;
  const sellingConfig = getResolvedProductSellingConfig(
    product as Parameters<typeof getResolvedProductSellingConfig>[0],
  );

  if (item.productType === EnumProductType.Event) {
    const isPackages =
      product?.eventMetadata?.ticketStructure ===
      EnumProductEventMetadataTicketStructure.Packages;

    if (isPackages) {
      const tierLines = (item.guestsPerTier ?? [])
        .filter(gt => gt && (gt.count ?? 0) > 0)
        .map(gt => {
          const tier =
            sellingConfig.options.find(
              option => String(option._id) === String(gt.tierId),
            ) ??
            (product?.productTiers ?? []).find(
              option => option && String(option._id) === String(gt.tierId),
            );
          const name = tier?.name
            ? getMultilingualString(tier.name, language)
            : "";
          const count = gt.count ?? 0;
          const unitPrice = tier?.price ?? 0;
          return {
            key: `${item.productId}-${gt.tierId}`,
            label: name
              ? qtyLabel(name, count)
              : qtyLabel(item.productName, count),
            amount: unitPrice * count,
            detail: options.eventDetail,
          };
        });

      if (tierLines.length > 0) {
        return scaleLinesToTotal(tierLines, item.total);
      }
      // No package picked yet: a $0 cart total would otherwise read as FREE.
      return [];
    }

    return [
      {
        key: item.productId,
        label: qtyLabel(item.productName, item.quantity),
        amount: item.total,
        detail: options.eventDetail,
      },
    ];
  }

  if (item.productType === EnumProductType.Merch) {
    return merchItemToSummaryLines(item);
  }

  if (
    item.productType === EnumProductType.Fee ||
    item.productType === EnumProductType.RenewableFee
  ) {
    const tier = selectedTierId
      ? sellingConfig.options.find(
          option => String(option._id) === String(selectedTierId),
        )
      : undefined;
    // Options exist but the customer hasn't picked one. The cart total is $0
    // until then, which would show the product (or a tier name) as FREE.
    if (sellingConfig.options.length > 0 && !tier) {
      return [];
    }
    const name = tier?.name
      ? getMultilingualString(tier.name, language)
      : item.productName;

    return [
      {
        key: `${item.productId}-${selectedTierId ?? "base"}`,
        label: qtyLabel(name, item.quantity),
        amount: item.total,
      },
    ];
  }

  // Accommodation and anything else: caller keeps the existing single-row UI.
  return null;
};

/** Keep line amounts proportional if they drift from the cart item total. */
const scaleLinesToTotal = (
  lines: SummaryLine[],
  total: number,
): SummaryLine[] => {
  const sum = lines.reduce((acc, line) => acc + line.amount, 0);
  if (sum === total || sum === 0) return lines;

  let allocated = 0;
  return lines.map((line, index) => {
    if (index === lines.length - 1) {
      return { ...line, amount: total - allocated };
    }
    const amount = Math.round((line.amount / sum) * total);
    allocated += amount;
    return { ...line, amount };
  });
};
